/**
 * Chapter rail — built from the page's own [data-chapter] sections — and,
 * on the home page, the way out: "Skip the story".
 *
 * @module chrome/rail
 */

import { $, $$, mount, pad } from '../lib/dom.js';
import { ScrollTrigger } from '../lib/gsap.js';
import { html } from '../lib/html.js';
import { scrollToTarget } from '../lib/scroll.js';
import { arrowIcon } from '../components/icons.js';

/**
 * @param {object} [options]
 * @param {{ label: string, href: string }} [options.skip] - The way out of the story.
 * @param {import('../lib/html.js').Markup | ''} [options.badge] - An award badge on the right edge.
 */
export function renderRail({ skip, badge = '' } = {}) {
    // disable badge displaying
    badge = '';

    const chapters = $$('[data-chapter]');

    if (!chapters.length) {
        return;
    }

    const links = chapters.map(
        (chapter, index) => html`<li>
            <a href="#${chapter.id}" data-rail-link="${chapter.id}">
                <span class="rail__number">${pad(index)}</span>
                <span class="rail__label">${chapter.dataset.railLabel}</span>
            </a>
        </li>`,
    );

    const holder = mount(
        'chapter-nav',
        html`<nav class="rail text-mono" aria-label="Chapters" data-rail><ol>${links}</ol></nav>
            ${skip && html`<a class="skip-story text-mono" href="${skip.href}" data-skip><span>${skip.label}</span>${arrowIcon()}</a>`}
            ${badge}`,
    );

    // A badge on the right edge pushes the rail aside.
    holder?.classList.toggle('has-award-badge', Boolean(badge));
}

export function animateRail() {
    const rail = $('[data-rail]');

    if (!rail) {
        return;
    }

    const skip = $('[data-skip]');

    // A pinned chapter lives in its pin spacer: that is what scrolls by.
    const box = (element) =>
        element.parentElement?.classList.contains('pin-spacer') ? element.parentElement : element;

    const items = $$('[data-rail-link]', rail)
        .map((link) => ({ link, section: document.getElementById(link.dataset.railLink) }))
        .filter((item) => item.section);

    if (!items.length) {
        return;
    }

    const setActive = (active) => {
        items.forEach(({ link }, index) => {
            link.classList.toggle('is-active', index === active);

            if (index === active) {
                link.setAttribute('aria-current', 'step');
            } else {
                link.removeAttribute('aria-current');
            }
        });
    };

    items.forEach(({ link, section }, index) => {
        ScrollTrigger.create({
            trigger: box(section),
            start: 'top center',
            end: 'bottom center',
            onToggle: (self) => self.isActive && setActive(index),
        });

        link.addEventListener('click', (event) => {
            event.preventDefault();
            scrollToTarget(box(section));
        });
    });

    setActive(0);

    // Shown only while the chapters are on screen, and gone as soon as the
    // footer comes in: the story is over, and "Back to top" sits right
    // where "Skip the story" would be.
    let inChapters = false;
    let footerInView = false;

    const update = () => {
        const visible = inChapters && !footerInView;
        rail.classList.toggle('is-on', visible);
        skip?.classList.toggle('is-on', visible);
    };

    const range = ScrollTrigger.create({
        trigger: box(items[0].section),
        endTrigger: box(items[items.length - 1].section),
        start: 'top bottom',
        end: 'bottom 40%',
        onToggle: (self) => {
            inChapters = self.isActive;
            update();
        },
    });

    const footer = $('.site-footer');

    if (footer) {
        ScrollTrigger.create({
            trigger: footer,
            start: 'top bottom',
            end: 'max',
            onToggle: (self) => {
                footerInView = self.isActive;
                update();
            },
        });
    }

    inChapters = range.isActive || window.scrollY < 10;
    update();
}
